import { CancelTokenSource } from 'axios';

import {
  HttpHeader,
  RateQuote,
  RateSelectionReason,
  RateShop,
  Shipment,
  ShipOrderSearchResult,
} from '@/model';
import {
  IConsolidatedShipment,
  ShipOrderSearchCriteria,
} from '@/model/consolidation';
import {
  ProposedShipment,
  ProposedShipmentCreationResult,
  ProposedShipmentRateResponse,
} from '@/model/routeBuilder';

import { BaseHttpController } from './BaseHttpController';

interface ICreateConsolidatedShipmentCommand {
  shipment: IConsolidatedShipment;
  rateQuoteId?: number;
  rateSelectionReason?: RateSelectionReason;
  skipAutoAction?: boolean;
}

export class ShipmentPlanningConsolidationController extends BaseHttpController {
  static readonly ENDPOINT: string = 'shipment-planning/consolidation';

  constructor() {
    super(ShipmentPlanningConsolidationController.ENDPOINT);
  }

  async search(
    criteria: ShipOrderSearchCriteria,
    cancelToken?: CancelTokenSource,
  ): Promise<ShipOrderSearchResult> {
    const response = await this.http.post<ShipOrderSearchResult>(
      'search',
      criteria,
      { cancelToken: cancelToken?.token },
    );
    return new ShipOrderSearchResult(response.data);
  }

  async createConsolidatedShipment(
    consolidatedShipment: IConsolidatedShipment,
    rateQuote?: RateQuote,
    rateSelectionReason?: RateSelectionReason,
    skipAutoAction = true,
  ): Promise<{ shipmentId: number }> {
    const command: ICreateConsolidatedShipmentCommand = {
      shipment: consolidatedShipment,
      rateQuoteId: rateQuote?.id,
      rateSelectionReason,
      skipAutoAction,
    };
    const response = await this.http.post<{ shipmentId: number }>(
      'create',
      command,
      {
        headers: {
          [HttpHeader.SOURCE]: 'Shipment Builder',
        },
      },
    );
    return response.data;
  }

  async createProposedShipmentBeta(
    proposedShipment: ProposedShipment,
    rateQuote?: RateQuote,
    rateSelectionReason?: RateSelectionReason,
    skipAutoAction = false,
  ): Promise<ProposedShipmentCreationResult> {
    const response = await this.http.post<ProposedShipmentCreationResult>(
      'create/beta',
      {
        shipment: proposedShipment,
        rateQuoteId: rateQuote?.id,
        rateSelectionReason,
        skipAutoAction,
      },
      {
        headers: {
          [HttpHeader.SOURCE]: 'Route Builder',
        },
      },
    );

    return new ProposedShipmentCreationResult(response.data);
  }

  async getRates(shipment: IConsolidatedShipment): Promise<RateShop> {
    const command = { shipment };
    const response = await this.http.post<RateShop>('rate', command);
    return new RateShop(response.data);
  }

  async getRatesBeta(
    shipment: ProposedShipment,
  ): Promise<ProposedShipmentRateResponse> {
    const command = { shipment };
    const response = await this.http.post<ProposedShipmentRateResponse>(
      'rate/beta',
      command,
    );
    return new ProposedShipmentRateResponse(response.data);
  }

  async modifyShipOrders(
    shipmentId: number,
    shipOrderIdsToAdd: number[],
    shipOrderIdsToRemove: number[] = [],
  ): Promise<Shipment> {
    const command = { shipmentId, shipOrderIdsToAdd, shipOrderIdsToRemove };
    const response = await this.http.post<Shipment>('ship-order', command);
    return new Shipment(response.data);
  }
}
